import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Stack,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { deleteInterstitialCampaign, getInterstitialCampaigns } from '../Api';
import type { InterstitialCampaignDto } from '../models/dtos/InterstitialCampaignDto';

type PageMessage = { type: 'success' | 'error'; text: string } | null;

export default function InterstitialCampaignsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [message, setMessage] = React.useState<PageMessage>(null);
    const [campaigns, setCampaigns] = React.useState<InterstitialCampaignDto[]>([]);

    const loadData = React.useCallback(async () => {
        setLoading(true);
        const campaignList = await getInterstitialCampaigns();
        setCampaigns(campaignList ?? []);
        setLoading(false);
    }, []);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this interstitial campaign?')) return;
        const result = await deleteInterstitialCampaign(id);
        if (!result.ok) {
            setMessage({ type: 'error', text: result.error ?? 'Delete failed.' });
            return;
        }
        setMessage({ type: 'success', text: 'Campaign deleted.' });
        await loadData();
    };

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
                    <Typography variant="h4">Custom interstitial campaigns</Typography>
                    <Button variant="contained" onClick={() => navigate('/marketing/interstitials/new')}>
                        New campaign
                    </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Full-screen HTML or video promos triggered by app events. Separate from AdMob interstitials and presentation overlays.
                </Typography>
                {message && <Alert severity={message.type} onClose={() => setMessage(null)}>{message.text}</Alert>}

                {loading ? <CircularProgress /> : (
                    <Stack spacing={2}>
                        {campaigns.map(campaign => (
                            <Card key={campaign.id}>
                                <CardContent>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Typography variant="subtitle1">{campaign.name}</Typography>
                                        <Chip size="small" label={campaign.creativeType} />
                                        <Chip size="small" color={campaign.enabled ? 'success' : 'default'} label={campaign.enabled ? 'Enabled' : 'Disabled'} />
                                        <Chip size="small" label={`priority ${campaign.priority}`} />
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Triggers: {campaign.triggerEvents.join(', ') || '—'}
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    <Button size="small" onClick={() => navigate('/marketing/interstitials/' + encodeURIComponent(campaign.id) + '/edit')}>
                                        Edit
                                    </Button>
                                    <Button size="small" onClick={() => navigate('/marketing/interstitials/' + encodeURIComponent(campaign.id) + '/stats')}>
                                        Stats
                                    </Button>
                                    <Button size="small" color="error" onClick={() => handleDelete(campaign.id)}>
                                        Delete
                                    </Button>
                                </CardActions>
                            </Card>
                        ))}
                        {campaigns.length === 0 && <Typography color="text.secondary">No campaigns yet.</Typography>}
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}
